import { useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => onAuthStateChanged(auth, async (nextUser) => {
    try {
      setUser(nextUser);
      if (!nextUser) {
        setIsAdmin(false);
      } else {
        // Founder roles are custom claims. Force a refresh so a recently granted
        // role is recognized without waiting for Firebase's cached token to expire.
        const token = await nextUser.getIdTokenResult(true);
        const hasAdminRole = token.claims.admin === true;
        setIsAdmin(hasAdminRole);
        setError(hasAdminRole ? '' : 'The refreshed Firebase token did not contain the founder role.');
      }
    } catch (verificationError) {
      setIsAdmin(false);
      setError(`Founder access verification failed (${verificationError.code || 'auth/unknown'}).`);
    } finally {
      setLoading(false);
    }
  }, (authError) => { setError(authError.message); setLoading(false); }), []);

  const signIn = async () => {
    setError('');
    try {
      auth.languageCode = 'en';
      await signInWithPopup(auth, googleProvider);
    } catch (signInError) {
      if (signInError.code === 'auth/popup-closed-by-user') return;
      if ([
        'auth/popup-blocked',
        'auth/cancelled-popup-request',
        'auth/operation-not-supported-in-this-environment',
        'auth/web-storage-unsupported'
      ].includes(signInError.code)) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      if (signInError.code === 'auth/unauthorized-domain') {
        setError('This website domain is not authorized for sign-in. Contact LeaveNOW support.');
        return;
      }
      const diagnosticCode = signInError.code || 'auth/unknown';
      setError(`Google sign-in failed (${diagnosticCode}). Please send this code to LeaveNOW support.`);
    }
  };
  const signOut = () => firebaseSignOut(auth);
  const refreshAccess = async () => {
    setLoading(true);
    setError('');
    try {
      await auth.currentUser?.getIdToken(true);
      window.location.reload();
    } catch {
      setError('Firebase could not refresh this account token. Sign out and sign in again.');
      setLoading(false);
    }
  };
  return { user, isAdmin, loading, error, signIn, signOut, refreshAccess };
}
